import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faBuilding,
  faUtensils,
  faCarSide,
  faTicketAlt,
  faUser,
  faUsers,
  faBed,
  faChild,
  faPlane
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { formatCurrency as formatCost } from "../../utils/currencyFormatter";
import { calculatePackageTotals } from "./pricingUtils";

const DesktopPackageCard = ({ pkg, estimateData, tripData, isBestValue, isMostPopular }) => {
  const currency = estimateData?.currency || "INR";
  const pax = estimateData?.paxBreakdown || estimateData?.occupancy?.paxBreakdown || {};
  const totalPax = estimateData?.totalPax || estimateData?.occupancy?.totalPax || 1;

  const a = pax.A || 0;
  const aeb = pax.AEB || 0;
  const cwb = pax.CwB || 0;
  const cnb = pax.CnB || 0;

  const { 
    includeTransport, transportAmount, 
    grandTotalMid, grandTotalMin, grandTotalMax,
    perPersonDoubleSharingMin, perPersonDoubleSharingMid, perPersonDoubleSharingMax,
    adultCostMid: adultCost, aebCostMid: aebCost, cwbCostMid: cwbCost, cnbCostMid: cnbCost
  } = calculatePackageTotals(pkg, estimateData);

  // Extract stars
  const starsMatch = pkg.name?.match(/^(\d)\s*Star/i);
  const stars = starsMatch ? parseInt(starsMatch[1], 10) : 3;
  const renderStars = () => {
    return Array.from({ length: 5 }).map((_, i) => (
      <FontAwesomeIcon
        key={i}
        icon={faStar}
        className={`w-3.5 h-3.5 ${i < stars ? "text-teal-600" : "text-gray-300"}`}
      />
    ));
  };

  const formatMealPlanText = (name) => {
    if (!name) return "Breakfast";
    const upper = name.toUpperCase();
    if (upper.includes("MAP")) return "Breakfast + Dinner";
    if (upper.includes("AP")) return "All Meals";
    if (upper.includes("EP")) return "Room Only";
    return "Breakfast";
  };
  const mealPlanText = formatMealPlanText(pkg.name);

  // Nights, Cities, Rooms
  const destinationsArray = tripData?.destinations && tripData.destinations.length > 0 
    ? tripData.destinations 
    : (tripData?.destination ? [tripData.destination] : []);
  const numCities = destinationsArray.length || 1;
  const numRooms = tripData?.rooms?.length || 1;
  const startDate = tripData?.startDate ? new Date(tripData.startDate) : null;
  const endDate = tripData?.endDate ? new Date(tripData.endDate) : null;
  let nights = 1;
  if (startDate && endDate) {
    const diffTime = Math.abs(endDate - startDate);
    nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  if (nights === 0) nights = 1;

  const themeColor = isMostPopular ? "blue" : "teal";
  const badgeColor = isMostPopular ? "bg-[#104A7D]" : "bg-[#0F826B]";
  const priceColor = isMostPopular ? "text-[#104A7D]" : "text-[#0F826B]";

  return (
    <div className="hidden lg:flex flex-col bg-white border border-[#e8ebf3] rounded-xl relative overflow-hidden h-full shadow-sm">
      {/* Top Right Ribbon */}
      {(isBestValue || isMostPopular) && (
        <div className={`absolute top-0 right-0 ${badgeColor} text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1.5 uppercase`}>
          <FontAwesomeIcon icon={faStar} className="w-2.5 h-2.5" /> 
          {isBestValue ? "Best Value" : "Most Popular"}
        </div>
      )}

      <div className="p-6 flex-1 flex flex-col">
        {/* Header (Stars & Pill) */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex gap-0.5">{renderStars()}</div>
          <span className="text-[9px] font-bold text-teal-800 uppercase bg-teal-50 px-2 py-1 rounded">
            {mealPlanText}
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-1 pr-20">
          {stars} Star + {mealPlanText}
        </h3>
        <p className="text-xs text-gray-500 mb-6">{nights} nights • {numCities} cities • {numRooms} rooms</p>

        {/* Big Totals (GRAND TOTAL vs PER PERSON) */}
        <div className="flex flex-col mb-6">
          {/* Labels Row */}
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] font-bold text-gray-400 uppercase">Grand Total</span>
            <span className="text-[10px] font-bold text-gray-400 uppercase">Per Person</span>
          </div>
          
          {/* Prices Row */}
          <div className="flex justify-between items-baseline mb-1">
            <span className="text-[26px] font-bold text-gray-900 leading-none">
              {grandTotalMin ? `${formatCost(grandTotalMin, currency)} - ${formatCost(grandTotalMax, currency)}` : formatCost(grandTotalMid, currency)}
            </span>
            <div className="flex items-baseline gap-1">
              <span className={`text-[17px] font-bold ${priceColor} leading-none`}>
                {perPersonDoubleSharingMin ? `${formatCost(perPersonDoubleSharingMin, currency)} - ${formatCost(perPersonDoubleSharingMax, currency)}` : formatCost(perPersonDoubleSharingMid || 0, currency)}
              </span>
              <span className="text-[11px] text-gray-400 font-medium">/avg</span>
            </div>
          </div>
          
          {/* Subtext Row */}
          <span className="text-xs text-gray-500">for full group ({totalPax} guests)</span>
        </div>

        {/* Per Pax Type Breakdown */}
        <div className="flex flex-col mb-6 bg-[#f8fafe] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <FontAwesomeIcon icon={faUsers} className="text-gray-400 w-3 h-3" />
            <span className="text-[10px] font-bold text-gray-500 uppercase">Per-Pax-Type Breakdown</span>
          </div>

          <div className="flex flex-col gap-3">
            {a > 0 && (
              <div className="flex justify-between items-center text-xs">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faUser} className={`text-${themeColor}-600 w-3.5 h-3.5`} />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-700">Per Person <span className="font-normal text-gray-500">(Double Sharing)</span></span>
                    <span className="text-[10px] text-gray-500">{formatCost(adultCost, currency)} × {a} Pax</span>
                  </div>
                </div>
                <span className="font-bold text-gray-900">{formatCost(adultCost * a, currency)}</span>
              </div>
            )}
            {aeb > 0 && (
              <div className="flex justify-between items-center text-xs mt-1">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faBed} className="text-blue-600 w-3.5 h-3.5" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-700">Per Adult Extra Bed</span>
                    <span className="text-[10px] text-gray-500">{formatCost(aebCost, currency)} × {aeb} Pax</span>
                  </div>
                </div>
                <span className="font-bold text-gray-900">{formatCost(aebCost * aeb, currency)}</span>
              </div>
            )}
            {cwb > 0 && (
              <div className="flex justify-between items-center text-xs mt-1">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faChild} className="text-green-600 w-3.5 h-3.5" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-700">Per Child With Bed</span>
                    <span className="text-[10px] text-gray-500">{formatCost(cwbCost, currency)} × {cwb} Pax</span>
                  </div>
                </div>
                <span className="font-bold text-gray-900">{formatCost(cwbCost * cwb, currency)}</span>
              </div>
            )}
            {cnb > 0 && (
              <div className="flex justify-between items-center text-xs mt-1">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faChild} className="text-yellow-600 w-3.5 h-3.5" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-700">Per Child No Bed</span>
                    <span className="text-[10px] text-gray-500">{formatCost(cnbCost, currency)} × {cnb} Pax</span>
                  </div>
                </div>
                <span className="font-bold text-gray-900">{formatCost(cnbCost * cnb, currency)}</span>
              </div>
            )}
          </div>
        </div>

        <hr className="border-[#e8ebf3] mb-6" />

        {/* Cost Breakdown Line Items */}
        <div className="flex flex-col mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FontAwesomeIcon icon={faBuilding} className="text-gray-400 w-3 h-3" />
            <span className="text-[10px] font-bold text-gray-500 uppercase">Cost Breakdown</span>
          </div>

          <div className="flex flex-col gap-3 text-xs">
            <div className="flex justify-between items-center text-gray-700">
              <span className="flex items-center gap-2.5">
                <FontAwesomeIcon icon={faBed} className="w-3.5 text-gray-400" /> Hotel Accommodation 
                <span className="w-1.5 h-1.5 rounded-full bg-[#0F826B]"></span>
              </span>
              <span className="font-medium text-gray-900">{formatCost(pkg.breakdown?.hotelMeal || 0, currency)}</span>
            </div>
            <div className="flex justify-between items-center text-gray-700">
              <span className="flex items-center gap-2.5">
                <FontAwesomeIcon icon={faUtensils} className="w-3.5 text-gray-400" /> Meal Plan <span className="text-[10px] text-gray-400">({mealPlanText})</span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#0F826B]"></span>
              </span>
              <span className="font-bold text-[#0F826B]">Included</span>
            </div>
            <div className="flex justify-between items-center text-gray-700">
              <span className="flex items-center gap-2.5">
                <FontAwesomeIcon icon={faTicketAlt} className="w-3.5 text-gray-400" /> Activities <span className="text-[10px] text-gray-400">(Shared)</span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#0F826B]"></span>
              </span>
              <span className="font-medium text-gray-900">{formatCost(pkg.breakdown?.activities || 0, currency)}</span>
            </div>
            <div className="flex justify-between items-center text-gray-700">
              <span className="flex items-center gap-2.5">
                <FontAwesomeIcon icon={faCarSide} className="w-3.5 text-gray-400" /> Transfers <span className="text-[10px] text-gray-400">(Airport + Local)</span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#0F826B]"></span>
              </span>
              <span className="font-medium text-gray-900">{formatCost(pkg.breakdown?.transfers || 0, currency)}</span>
            </div>
            {includeTransport && (
              <div className="flex justify-between items-center text-gray-700">
                <span className="flex items-center gap-2.5">
                  <FontAwesomeIcon icon={faPlane} className="w-3.5 text-gray-400" /> Transport <span className="text-[10px] text-gray-400">(Flights / Trains)</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0F826B]"></span>
                </span>
                <span className="font-medium text-gray-900">{formatCost(transportAmount, currency)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto">
          <div className="flex justify-between items-center">
            <span className="font-bold text-gray-900 uppercase text-xs">Grand Total</span>
            <span className="text-2xl font-bold text-gray-900">{formatCost(grandTotalMid, currency)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopPackageCard;
